import { createAsyncThunk } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'
import {
  getChargeMDM,
  getPatientsVisitDetailsByVisitId,
  getVisitHistory,
  saveBillingNotes,
  updateChargeMDM,
} from '../../helper/backendHelper'
import { dateDbFormat } from '../../utils/dateTimeUtils'
import { VISIT_STATUS } from '../../utils/statusConstants'
import { showLongTermCare } from '../../utils/sharedFeaturesFunctions'
import { COLLECTOR_URL } from '../../apiHelper/apiRoute/collector'
import { getCollector, postCollector, putCollector } from '../../apiHelper/collectorApi'
import { deformatPhoneNumber } from '../../utils/sharedFunctions'
import FEATURES from '../../utils/featuresConstants'
import { getQualityMips } from '../../helper/mipsHelper'
import { getFusion } from '../../apiHelper/fusionApi'

export const getEncounter = createAsyncThunk(
  'sidePanel/getEncounter',
  async (encounterId, thunkAPI) => {
    try {
      if (!encounterId) {
        return null
      }

      if (Boolean(thunkAPI.getState()?.sidePanel?.panelEncounter)) {
        return thunkAPI.rejectWithValue(null)
      }

      const isLast = thunkAPI.getState()?.sidePanel?.isLastEncounter

      const results = await Promise.allSettled([
        getPatientsVisitDetailsByVisitId(encounterId).then((res) => res?.result?.[0]),
        getVisitHistory(encounterId).then((res) => res?.resData),
        isLast
          ? null
          : getCollector(COLLECTOR_URL.GET_PROGRESS_NOTE.replace('{visitID}', encounterId)).then(
              (res) => (res?.status === 'failure' ? null : res?.data),
            ),
      ])
      const [currentVisit, visitHistory, currentChart] = results.map((result) => {
        if (result.status === 'fulfilled') {
          return result.value
        }
        console.error(result.reason)
        return null
      })

      setTimeout(() => thunkAPI.dispatch(updatePanelMips()), 1000)

      return {
        currentVisit,
        visitHistory,
        currentChart,
      }
    } catch (err) {
      console.error('getting error in rendering sidePanel', err)
      return null
    }
  },
)

export const refreshEncounter = createAsyncThunk(
  'sidePanel/refreshEncounter',
  async ({ encounterId, maintainLastEncounter = false }, thunkAPI) => {
    try {
      // refetch current encounter and chart
      const results = await Promise.allSettled([
        getPatientsVisitDetailsByVisitId(encounterId).then((res) => res?.result?.[0]),
        getCollector(COLLECTOR_URL.GET_PROGRESS_NOTE.replace('{visitID}', encounterId)).then(
          (res) => (res?.status === 'failure' ? null : res?.data),
        ),
      ])
      const [refreshedEncounter, refreshedChart] = results.map((result) => {
        if (result.status === 'fulfilled') {
          return result.value
        }
        console.error(result.reason)
        return null
      })

      return { refreshedEncounter, refreshedChart, maintainLastEncounter }
    } catch (err) {
      console.error('getting error in saving sidePanel refreshEncounter', err)
      return null
    }
  },
)

const VALID_MDM_KEYS = ['complexity', 'data', 'risk']
export const updateMDM = createAsyncThunk('sidePanel/updateMDM', async (updatedMDM, thunkAPI) => {
  try {
    const encounter = thunkAPI.getState()?.sidePanel?.panelEncounter

    // fetch MDM if empty body
    if (Object.keys(updatedMDM).length === 0) {
      const mdmValues = await getChargeMDM(encounter?._id)
      if (mdmValues?.status === 'failure' || mdmValues?.response?.status) {
        // failed
        return thunkAPI.rejectWithValue(mdmValues?.response?.data)
      }
      return mdmValues
    }

    // update MDM if body not empty
    const reformattedMDM = {}
    VALID_MDM_KEYS.forEach((mdmKey) => {
      reformattedMDM[mdmKey] = {
        user_level: updatedMDM[mdmKey]?.user_level || updatedMDM[mdmKey]?.level,
      }
    })

    let newMDM = await updateChargeMDM(encounter?._id, reformattedMDM)

    if (newMDM?.status === 'failure' || newMDM?.response?.status) {
      // failed
      return thunkAPI.rejectWithValue(newMDM.response?.data)
    }

    return newMDM
  } catch (err) {
    console.error('getting error in saving sidePanel updateMDM', err)
    return null
  }
})

export const savePatientDetails = createAsyncThunk(
  'sidePanel/savePatientDetails',
  async (data, thunkAPI) => {
    try {
      const encounter = thunkAPI.getState()?.sidePanel?.panelEncounter
      let payloadEdit = {
        dob:
          data.DOB || encounter?.patient_dob
            ? dateDbFormat(data.DOB || encounter?.patient_dob)
            : '',
        gender: data.gender || encounter?.gender || 'U',
        ethnicity: data.ethnicity || encounter?.ethnicity,
        genderIdentity: data.genderIdentity || encounter?.genderIdentity,
        preferredLanguage: data.preferredLanguage || encounter?.preferredLanguage,
        race: data.race || encounter?.race,
        sexualOrientation: data.sexualOrientation || encounter?.sexualOrientation,
        amendments: data.amendments || encounter?.amendments,
        // primary_care_physician: data.primary_care_physician || encounter?.primary_care_physician,
        first_name: data.firstname || encounter?.patient_firstname,
        last_name: data.lastname || encounter?.patient_lastname,
        middle_name: data.middlename || encounter?.patient_middlename,
        hospice_related: Boolean(encounter?.hospice_related),
        acoEnrolled: Boolean(data.acoEnrolled || encounter?.patient_acoEnrolled),
        hospiceEnrolled: Boolean(data.hospiceEnrolled || encounter?.patient_hospiceEnrolled),
        admission_reason: encounter?.admission_reason || '',
        cellphone: deformatPhoneNumber(encounter.patient_cellphone || ''),
        room: encounter?.facility_mrn || '',
        patient_admit_info: [
          {
            facility_id: data.facility_id || encounter?.facility_id || '',
            facility_name: data.facility_name || encounter?.facility_name || '',
            facility_mrn: data.facility_mrn || encounter?.facility_mrn || '',
            admission_date: data.admission_date || encounter?.admission_date || null,
            discharge_date: data.discharge_date || encounter?.discharge_date || null,
          },
        ],
        // encounter?.patient_admit_info?.map((pai) => ({
        //   facility_id: pai.facility_id || '',
        //   facility_name: pai.facility_name || '',
        //   facility_mrn: pai.facility_mrn || '',
        //   admission_date: pai.admission_date || null,
        //   discharge_date: pai.discharge_date || null,
        // })) || [],
        //

        //     [
        //   {
        //     facility_id: data.selectedFacility?._id || '',
        //     facility_name: data.selectedFacility?.name || '',
        //     admission_date:
        //       data.DOI !== null && data.DOI !== ''
        //         ? format(new Date(data.DOI), 'yyyy-MM-dd')
        //         : '',
        //     facility_mrn: data.FacilityMRN?.trim(),
        //   },
        // ],
      }

      const url = COLLECTOR_URL.EDIT_PATIENT.replace('{patient_id}', `${encounter?.patient_id}`)
      let res = await putCollector(url, payloadEdit)

      if (res.status === 'success') {
        thunkAPI.dispatch(
          refreshEncounter({ encounterId: encounter?._id, maintainLastEncounter: true }),
        )
      } else {
        // TODO toast?
      }
    } catch (err) {
      console.error('getting error in saving sidePanel patient', err)
      return null
    }
  },
)

let isCloning = false
export const saveEncounterField = createAsyncThunk(
  'sidePanel/saveEncounterField',
  async (data, thunkAPI) => {
    try {
      if (isCloning) {
        return data
      }

      const isLast = thunkAPI.getState()?.sidePanel?.isLastEncounter
      const encounter = thunkAPI.getState()?.sidePanel?.panelEncounter
      const features = thunkAPI.getState()?.user?.features || []

      let procedures = encounter.procedureCodes?.map((proc) => ({
        code: proc.code + '' || '',
        modifiers: proc.modifiers,
        units: proc?.units,
      }))

      if (isLast) {
        // clone
        isCloning = true
        // since we are cloning, save our payload to the store
        // to avoid being overwritten by other dispatches
        thunkAPI.dispatch(saveEncounterField(data))

        const url = COLLECTOR_URL.CLONE.replace('{visitId}', `${encounter._id}`)
        const req = {
          service_date:
            data.field === 'service_date_from' ? data.value : encounter.service_date_from,
          status: VISIT_STATUS['checked_in'],
          last_visit: null,
          procedures: procedures,
          encounterType: encounter?.encounterType || 'Subsequent',
        }
        const res = await postCollector(url, req)
        isCloning = false

        if (res.status === 'failure') {
          const msg = res?.message || 'Failed to create visit'
          toast.error(msg)
          return thunkAPI.rejectWithValue({ type: 'clone', error: msg })
        } else {
          toast.success('New Visit Created')
        }

        // in case any other actions have occurred during our clone
        // re-save the encounter after the store has updated our _id
        setTimeout(() => {
          thunkAPI.dispatch(saveEncounterField({}))
        }, 1000)

        return {
          cloneUpdates: [
            // values to update that the clone set
            { payload: { field: '_id', value: res.result._id } },
            { payload: { field: 'status', value: res.result.status } },
            {
              payload: { field: 'rendering_provider_id', value: res.result.rendering_provider_id },
            },
            {
              payload: {
                field: 'rendering_provider_name',
                value: res.result.rendering_provider_name,
              },
            },
          ],
        }
      } else {
        if (!encounter.facility_id) {
          return toast.error('No facility selected')
        }

        // if (selectedTemplate?.template) {
        //   data.note_template = selectedTemplate?.template
        // }

        // Make sure that our visit does not remain in an 'Admit' Status
        if (encounter?.status === 'Admit') {
          const url = COLLECTOR_URL.VISIT_STATUS.replace('{visitId}', encounter?._id)
          await putCollector(
            url,
            // the status we would prefer the visit to update to
            // if its in an 'unseen' state
            { status: 'Checked In' },
          )
        }

        // save
        const saveBody = {
          admission_date: encounter?.admission_date || '',
          admission_reason: encounter?.admission_reason || '',
          // VERIFY
          // only append admitPatientEvent if the feature is on, and it has been pulled in
          ...(features?.includes(FEATURES.InsuranceMapper) &&
            encounter?.admitPatientEvent?._id && {
              admitPatientEvent: encounter?.admitPatientEvent._id,
            }),
          // verify TODO
          ...(features?.includes(FEATURES.InsuranceMapper) &&
            encounter?.dischargePatientEvent?._id && {
              dischargePatientEvent: encounter?.dischargePatientEvent._id,
            }),
          appointment_duration: '',
          appointment_time: '',
          care_coordination: Boolean(encounter.care_coordination),
          careteam: encounter.careteam || '',
          charge_id: encounter._id,
          code_assist: encounter?.code_assist || {
            diagnosis_notes: '',
            billing_notes: '',
            audio_notes: [],
            templates: [],
          },
          diagnosis_codes: encounter.diagnosisCodes.map((item) => item.code) || [],
          discharge_date: encounter?.discharge_date || '',
          encounterType: encounter.encounterType || 'Initial',
          facility_id: encounter.facility_id,
          facility_mrn: encounter?.facility_mrn || '',
          files: [], //files,
          // verify
          facility_name: encounter.facility_name,
          hospice_related: Boolean(encounter.hospice_related),
          incident_to_provider_id: encounter.incident_to_provider_id,
          incident_to_provider_substantive: encounter.incident_to_provider_id
            ? encounter.incident_to_provider_substantive
            : null,
          // only add to object if not null
          ...(encounter?.legal_entity_id && {
            legal_entity_id: encounter?.legal_entity_id,
          }),
          long_term_care:
            showLongTermCare(features, encounter?.place_of_service) && encounter.long_term_care,
          patient: encounter.pData,
          patient_id: encounter.patient_id,
          place_of_service: encounter?.place_of_service,
          // verify
          procedure_codes: procedures,
          references: encounter.references,
          room: encounter?.room && encounter?.room !== '-' ? encounter?.room : '',
          service_date: dateDbFormat(encounter.service_date_from),
          status: new Date(encounter.service_date_from).toDateString()
            ? VISIT_STATUS['checked_in']
            : VISIT_STATUS['scheduled'],
          telehealth: Boolean(encounter.telehealth),
          isSaveOnly: true,
        }

        let res = await postCollector(COLLECTOR_URL.ADD_ENCOUNTER, saveBody)

        if (res?.status === 'success') {
          thunkAPI.dispatch(updatePanelMips())
          let chargeID = res?.result?._id

          // await handleNewPatientAdmitEvent(chargeID)
        } else {
          if (res?.result || res.response?.data?.message) {
            let message = res?.result || res.response?.data?.message
            if (res.response?.data?.message?.includes('facility_mrn')) {
              message =
                'Facility MRN must be less than 15 characters, and only have letters, numbers, or hyphens'
            }
            toast.error(message)
          }
        }
      }

      return {
        ...data,
      }
    } catch (err) {
      console.error('getting error in rendering sidePanel', err)
      return null
    }
  },
)

export const updatePanelMips = createAsyncThunk(
  'sidePanel/updatePanelMips',
  async (_, thunkAPI) => {
    try {
      const practiceId = thunkAPI.getState()?.user?.practice?._id
      const encounterId = thunkAPI.getState()?.sidePanel?.panelEncounter?._id
      const features = thunkAPI.getState()?.user?.features || []

      if (features?.includes(FEATURES.VisitNotesMIPS) && encounterId && practiceId) {
        const newMips = await getQualityMips(encounterId, practiceId)
        return (newMips || []).some((mip) => !mip.value)
      }

      return false
    } catch (err) {
      console.error('getting error in updating sidePanel mips', err)
      return null
    }
  },
)
export const performAiCodeCheck = createAsyncThunk(
  'sidePanel/performAiCodeCheck',
  async (_, thunkAPI) => {
    let {
      user: { features },
      sidePanel,
    } = thunkAPI.getState()
    if (sidePanel?.panelChart?._id) {
      const res = await getFusion('v1/chart/inspect/' + sidePanel.panelChart._id)

      if (res?.status !== 'success') {
        console.error(res)
        return null
      } else {
        return res.data
      }
    }
    return null
  },
)

// const handleEncounterSaveDraft = async (onSuccess = () => {}) => {
//   let chosenFacility = allFacilities?.find(
//       (item) => item?._id === selectedFacilityId && item.enabled,
//   )
//   if (!chosenFacility) {
//     return toast.error(
//         'Unable to find chosen facility. Please reselect a facility on this page or on the census page',
//     )
//   }
//
//   let pos = placeOfService || chosenFacility?.place_of_service
//   let procedures = selectedProcedure.map((proc) => ({
//     code: proc.code + '' || '',
//     modifiers: proc.modifiers,
//     units: proc?.units,
//   }))
//
//   setIsLoading(true)
//   const data = {
//     facility_id: selectedFacilityId,
//     facility_name: selectedFacilityName,
//     service_date: dateDbFormat(dateOfService),
//     appointment_time: '',
//     appointment_duration: '',
//     status: new Date(dateOfService).toDateString()
//         ? VISIT_STATUS['checked_in']
//         : VISIT_STATUS['scheduled'],
//     patient_id: patientIDNew || pid,
//     patient: pData,
//     diagnosis_codes: selectedDiagnosis.map((item) => item.code),
//     procedure_codes: procedures,
//     code_assist: {
//       diagnosis_notes: '',
//       billing_notes: billingTxt,
//       audio_notes: [],
//       templates: [],
//     },
//     careteam: careTeam,
//     telehealth: isTelehealth,
//     care_coordination: isCareCoordination,
//     long_term_care: showLongTermCare(features, pos) && isLongTermCare,
//     incident_to_provider_id: incidentToProviderId,
//     incident_to_provider_substantive: incidentToProviderId ? incidentToProviderSubstantive : null,
//     admission_date: admissionDate,
//     room: location !== '-' ? location : '',
//     references: pData.references,
//     place_of_service: pos,
//     // only add to object if not null
//     ...(billingEntity?.legal_entity_id && {
//       legal_entity_id: billingEntity?.legal_entity_id,
//     }),
//     // only append admitPatientEvent if the feature is on, and it has been pulled in
//     ...(features?.includes(FEATURES.InsuranceMapper) &&
//         pdata?.admitPatientEvent?._id && {
//           admitPatientEvent: pdata?.admitPatientEvent._id,
//         }),
//     hospice_related: isHospiceRelated,
//     admission_reason: pData?.admission_reason || '',
//     encounterType: initialBtn || 'Initial',
//     isSaveOnly: true,
//   }
//   if (canUseCurrentEncounterID) {
//     data.charge_id = pdata?._id
//   }
//   const addEncounterEndpoint = COLLECTOR_URL.ADD_ENCOUNTER
//
//   if (selectedTemplate?.template) {
//     data.note_template = selectedTemplate?.template
//   }
//
//   saveBillingNotes(pData?._id, {
//     billing_notes: billingTxt,
//   })
//
//   await updateVisitIfInAdmitState()
//
//   await postCollector(addEncounterEndpoint, data)
//       .then(async (res) => {
//         if (res?.status === 'success') {
//           let chargeID = res?.result?._id || pdata?._id
//
//           await handleNewPatientAdmitEvent(chargeID)
//           if (mipsValueData?.length > 0) {
//             if (features?.includes(FEATURES.VisitNotesMIPS)) {
//               let mipsPayload = mipsValueData
//                   ?.map((mips) => ({
//                     measureKey: mips?.measureKey,
//                     value: mips?.value?.label,
//                     option_data: mips?.value,
//                   }))
//                   ?.filter((item) => item?.value)
//
//               await saveMIPSData(chargeID, mipsPayload)
//             }
//           }
//           onSuccess()
//         } else {
//           if (res?.result || res.response?.data?.message) {
//             toast.error(res?.result || res.response.data.message)
//           }
//         }
//       })
//       .catch((e) => {
//         console.error(e)
//       })
//       .finally(() => {
//         setIsLoading(false)
//       })
// }
